/** QuickRouter — Fabrication : contrôle DRC, nomenclature (format JLCPCB), placement CMS,
 *  et dossier de fabrication à télécharger (Gerber + perçages + BOM/CPL), généré dans le
 *  navigateur avec le même code que le serveur. */
import { useMemo } from 'react';
import { useProjectStore } from '../store/useProjectStore.js';
import { runDrc } from '../../shared/drc.js';
import { fabricationFiles, fabricationZip, bomCsv } from '../../shared/export/fab.js';
import { polyBox, r4 } from '../../shared/geom.js';
import { stackupOf } from '../../shared/stackup.js';
import { lengthReport } from '../../shared/diffpair.js';

function download(name, data, type) {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([data], { type }));
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

export default function FabView() {
  const project = useProjectStore((s) => s.project);
  const setMode = useProjectStore((s) => s.setMode);
  const setDrc = useProjectStore((s) => s.setDrc);
  const lookup = useProjectStore.getState().lookup();
  const drc = useMemo(() => runDrc(project, lookup), [project]); // eslint-disable-line react-hooks/exhaustive-deps
  const fab = useMemo(() => { try { return fabricationFiles(project, lookup); } catch (e) { return { files: [], warnings: [`Export impossible : ${e.message}`] }; } }, [project]); // eslint-disable-line react-hooks/exhaustive-deps
  const bom = useMemo(() => { try { return bomCsv(project, lookup).rows ?? []; } catch { return []; } }, [project]); // eslint-disable-line react-hooks/exhaustive-deps
  const [x0, y0, x1, y1] = polyBox(project.board.outline);
  const pairs = useMemo(() => lengthReport(project).pairs, [project]);

  const zip = () => {
    const { zip: data, name } = fabricationZip(project, lookup);
    download(name, data, 'application/zip');
  };
  const show = () => { setDrc(drc); setMode('board'); };

  return (
    <div className="fab">
      <div className="fab-head">
        <h2>Fabrication — {project.name}</h2>
        <span className="muted">{r4(x1 - x0)} × {r4(y1 - y0)} mm · {project.board.layers} couches{project.board.layers > 2 ? ` (${stackupOf(project.board).id})` : ''} · {project.board.thickness} mm · vernis {project.board.color} · {project.board.finish}</span>
      </div>

      <section>
        <h3>Contrôle des règles (DRC)</h3>
        <div className={`drc-banner ${drc.ok ? 'ok' : 'ko'}`}>
          {drc.ok ? '✓ Aucune erreur' : `✗ ${drc.summary.errors} erreur(s)`} · {drc.summary.warnings} avertissement(s) · {drc.summary.routedNets}/{drc.summary.nets} nets routés
          <button onClick={show}>Voir sur la carte</button>
        </div>
        {drc.errors.length > 0 && <ul className="issues">{drc.errors.slice(0, 80).map((e, i) => <li key={i} className="err">{e.message}</li>)}</ul>}
        {drc.warnings.length > 0 && <ul className="issues">{drc.warnings.slice(0, 40).map((e, i) => <li key={i} className="warn">{e.message}</li>)}</ul>}
      </section>

      <section>
        <h3>Dossier de fabrication</h3>
        <p className="muted">Gerber RS-274X (cuivre, vernis, pâte, sérigraphie, contour), perçages Excellon PTH/NPTH, nomenclature et fichier de placement au format JLCPCB. Accepté tel quel par JLCPCB, PCBWay, Aisler, Eurocircuits.</p>
        <div className="row">
          <button className="primary" onClick={zip} disabled={!fab.files.length}>⤓ Télécharger le zip de fabrication</button>
          {!drc.ok && <span className="warn small">Le DRC signale des erreurs : la carte risque d'être fausse.</span>}
        </div>
        {fab.warnings.length > 0 && <ul className="issues">{fab.warnings.map((w, i) => <li key={i} className="warn">{w}</li>)}</ul>}
        <div className="files">
          {fab.files.map((f) => (
            <button key={f.name} className="file" title="Télécharger ce fichier seul" onClick={() => download(f.name, f.data, 'text/plain')}>
              <span className="grow mono">{f.name}</span><span className="muted">{(String(f.data).length / 1024).toFixed(1)} Ko</span>
            </button>
          ))}
        </div>
      </section>

      {pairs.length > 0 && (
        <section>
          <h3>Paires différentielles</h3>
          <table className="bom-table">
            <thead><tr><th>Paire</th><th className="n">Longueur P</th><th className="n">Longueur N</th><th className="n">Écart</th></tr></thead>
            <tbody>
              {pairs.map((x) => <tr key={x.p}><td className="mono">{x.p} / {x.n}</td><td className="n">{x.lengthP} mm</td><td className="n">{x.lengthN} mm</td><td className="n">{x.skew} mm</td></tr>)}
            </tbody>
          </table>
        </section>
      )}

      <section>
        <h3>Nomenclature</h3>
        <table className="bom-table">
          <thead><tr><th>Valeur</th><th>Repères</th><th>Empreinte</th><th>LCSC</th><th className="n">Qté</th></tr></thead>
          <tbody>
            {bom.map((l, i) => (
              <tr key={i}><td>{l.comment}</td><td className="mono">{l.refs.join(', ')}</td><td className="mono">{l.footprint}</td><td className="mono">{l.lcsc || <span className="muted">—</span>}</td><td className="n">{l.refs.length}</td></tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
